import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Loader2 } from 'lucide-react'
import { API_ROUTES, apiFetch } from '@/config/api-routes'
import { useAuth } from '@/contexts/AuthContext'

interface WorkspaceRow {
  id: string
  nome: string
  status: string
  created_at: string
  titular: { nome: string; email: string; crm: string | null } | null
}

export default function AdminMedicosPage() {
  const { enterWorkspaceAsAdmin } = useAuth()
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [abrindo, setAbrindo] = useState<string | null>(null)

  useEffect(() => {
    apiFetch(API_ROUTES.admin.workspaces)
      .then(r => r.json())
      .then(j => {
        if (j.error) throw new Error(j.error)
        setWorkspaces(j.workspaces ?? [])
      })
      .catch(e => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setLoading(false))
  }, [])

  async function abrirWorkspace(id: string) {
    setAbrindo(id)
    setErro(null)
    try {
      await enterWorkspaceAsAdmin(id)
    } catch {
      setErro('Não foi possível abrir o workspace.')
      setAbrindo(null)
    }
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Médicos e workspaces</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Contas provisionadas pela operação.</p>
        </div>
        <Link to="/admin/medicos/novo" className="btn-primary">Novo médico</Link>
      </header>

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        </div>
      ) : workspaces.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          Nenhum workspace provisionado ainda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {workspaces.map(w => (
            <div key={w.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{w.titular?.nome ?? '—'}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {w.nome} · {w.titular?.email ?? '—'}
                  {w.titular?.crm ? ` · CRM ${w.titular.crm}` : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                  Criado em {new Date(w.created_at).toLocaleDateString('pt-BR')}
                </div>
              </div>
              <span className={`badge ${w.status === 'ativo' ? 'badge-ok' : w.status === 'suspenso' ? 'badge-warn' : 'badge-muted'}`}>
                {w.status}
              </span>
              <Link to={`/admin/workspaces/${w.id}`} className="btn-secondary" style={{ minHeight: 36, fontSize: 13 }}>
                Detalhes <ChevronRight size={14} aria-hidden />
              </Link>
              <button
                type="button"
                className="btn-primary"
                style={{ minHeight: 36, fontSize: 13 }}
                disabled={abrindo === w.id}
                title="Você passará a navegar o app como este workspace, saindo do painel admin"
                onClick={() => abrirWorkspace(w.id)}
              >
                {abrindo === w.id ? 'Entrando…' : 'Entrar como este workspace'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
